import {
  CLIENT_ID,
  WHOOP_AUTH_URL,
  WHOOP_SCOPES,
  WHOOP_TOKEN_URL,
  getRedirectUri,
} from './config'
import { codeChallengeFromVerifier, generateCodeVerifier, generateState } from './pkce'
import { saveTokens, loadTokens, isExpired, clearTokens, type TokenSet } from './tokens'

const PKCE_VERIFIER_KEY = 'whooplens.pkce.verifier'
const PKCE_STATE_KEY = 'whooplens.pkce.state'

export async function startLogin(): Promise<void> {
  if (!CLIENT_ID) throw new Error('VITE_WHOOP_CLIENT_ID is not set')

  const verifier = generateCodeVerifier()
  const challenge = await codeChallengeFromVerifier(verifier)
  const state = generateState()

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
  sessionStorage.setItem(PKCE_STATE_KEY, state)

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: getRedirectUri(),
    scope: WHOOP_SCOPES,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })

  window.location.href = `${WHOOP_AUTH_URL}?${params.toString()}`
}

export async function completeLogin(searchParams: URLSearchParams): Promise<TokenSet> {
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const err = searchParams.get('error')
  if (err) throw new Error(`Whoop returned error: ${err}`)
  if (!code) throw new Error('Missing authorization code')

  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY)
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(PKCE_STATE_KEY)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)

  if (!expectedState || state !== expectedState) {
    throw new Error('OAuth state mismatch — possible CSRF attempt')
  }
  if (!verifier) throw new Error('Missing PKCE verifier — restart login')

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: getRedirectUri(),
    client_id: CLIENT_ID,
    code_verifier: verifier,
  })

  const res = await fetch(WHOOP_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    const txt = await res.text()
    throw new Error(`Token exchange failed: ${res.status} ${txt}`)
  }
  const json = (await res.json()) as RawTokenResponse
  const tokens = normalize(json)
  saveTokens(tokens)
  return tokens
}

interface RawTokenResponse {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
  scope?: string
}

function normalize(r: RawTokenResponse): TokenSet {
  return {
    access_token: r.access_token,
    refresh_token: r.refresh_token,
    token_type: r.token_type,
    expires_at: Date.now() + r.expires_in * 1000,
    scope: r.scope,
  }
}

export async function refreshIfNeeded(): Promise<TokenSet | null> {
  const tokens = loadTokens()
  if (!tokens) return null
  if (!isExpired(tokens)) return tokens
  if (!tokens.refresh_token) {
    // No refresh token — make user re-auth.
    clearTokens()
    return null
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: tokens.refresh_token,
    client_id: CLIENT_ID,
  })
  const res = await fetch(WHOOP_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    // Refresh failed (e.g. refresh_token revoked or too old). Force re-login.
    clearTokens()
    return null
  }
  const json = (await res.json()) as RawTokenResponse
  const next = normalize(json)
  // Whoop sometimes omits a new refresh_token; preserve the old one.
  if (!next.refresh_token) next.refresh_token = tokens.refresh_token
  saveTokens(next)
  return next
}

export function logout(): void {
  clearTokens()
  // Sync state survives in IndexedDB so re-login is incremental, not a full re-pull.
}
